import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useSmartBack } from '../../hooks/useSmartBack';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import {
  ArrowLeft, Loader2, Trophy, TrendingUp, TrendingDown, Minus,
  AlertTriangle, Star, ChevronRight, Search, Bell, BellOff,
  CheckCircle2, Clock, Sparkles, Zap, Users, BarChart3
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { key: 'punctuality', label: 'Punctuality', icon: Clock, desc: 'Arrived on time, ready to work' },
  { key: 'cleaning_quality', label: 'Cleaning Quality', icon: Sparkles, desc: 'Thoroughness and attention to detail' },
  { key: 'professionalism', label: 'Professionalism', icon: Users, desc: 'Communication and conduct' },
  { key: 'efficiency', label: 'Efficiency', icon: Zap, desc: 'Time management and workflow' },
  { key: 'client_satisfaction', label: 'Client Satisfaction', icon: Star, desc: 'Client feedback and demeanor' },
];

const TIER_STYLES = {
  Elite: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  Strong: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  Watch: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  Risk: 'bg-red-500/15 text-red-400 border-red-500/30',
  Unrated: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30',
};

const TIER_BG = {
  Elite: 'from-emerald-600 to-emerald-800',
  Strong: 'from-blue-600 to-blue-800',
  Watch: 'from-amber-600 to-amber-800',
  Risk: 'from-red-600 to-red-800',
  Unrated: 'from-zinc-600 to-zinc-800',
};

function ScoreRing({ pct, size = 80, tier }) {
  const radius = (size - 8) / 2;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (pct / 100) * circ;
  const colors = { Elite: '#10b981', Strong: '#3b82f6', Watch: '#f59e0b', Risk: '#ef4444', Unrated: '#71717a' };
  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#27272a" strokeWidth="6" />
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={colors[tier] || '#71717a'}
        strokeWidth="6" strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
        className="transition-all duration-1000" />
    </svg>
  );
}

function StarRating({ value, onChange, disabled }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} type="button" disabled={disabled}
          onClick={() => onChange(n)}
          className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all active:scale-90 ${
            n <= value ? 'bg-[#F56F05] text-white shadow-md shadow-orange-500/20' : 'bg-zinc-800 text-zinc-600 hover:bg-zinc-700'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          data-testid={`star-${n}`}>
          <Star className="h-4 w-4" fill={n <= value ? 'currentColor' : 'none'} />
        </button>
      ))}
    </div>
  );
}

export default function AdminPerformancePage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { back } = useSmartBack('/admin/dashboard');
  const [loading, setLoading] = useState(true);
  const [cleaners, setCleaners] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [tierFilter, setTierFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedCleaner, setSelectedCleaner] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Score dialog
  const [scoreOpen, setScoreOpen] = useState(false);
  const [scoreForm, setScoreForm] = useState({ appointment_id: '', cleaner_user_id: '', scores: {}, notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [completedAppts, setCompletedAppts] = useState([]);
  const [apptLoading, setApptLoading] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = useCallback(async () => {
    try {
      const [cleanersRes, alertsRes] = await Promise.all([
        axios.get(`${API_URL}/api/performance/cleaners`, { headers }),
        axios.get(`${API_URL}/api/performance/alerts?acknowledged=false`, { headers }),
      ]);
      setCleaners(cleanersRes.data);
      setAlerts(alertsRes.data);
    } catch (_) {
      toast.error('Failed to load performance data');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCleanerDetail = async (cleaner) => {
    setSelectedCleaner(cleaner);
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/performance/cleaners/${cleaner.cleaner_id}`, { headers });
      setDetailData(res.data);
    } catch { toast.error('Failed to load detail'); }
    finally { setDetailLoading(false); }
  };

  const openScoreDialog = async () => {
    setScoreOpen(true);
    setApptLoading(true);
    setScoreForm({ appointment_id: '', cleaner_user_id: '', scores: {}, notes: '' });
    try {
      const res = await axios.get(`${API_URL}/api/appointments?status=Completed`, { headers });
      setCompletedAppts(res.data.filter(a => a.assigned_cleaner_user_id));
    } catch { toast.error('Failed to load appointments'); }
    finally { setApptLoading(false); }
  };

  const handleApptSelect = (apptId) => {
    const appt = completedAppts.find(a => a.id === apptId);
    if (appt) {
      setScoreForm(f => ({ ...f, appointment_id: apptId, cleaner_user_id: appt.assigned_cleaner_user_id }));
    }
  };

  const handleSubmitScore = async () => {
    for (const cat of CATEGORIES) {
      if (!scoreForm.scores[cat.key] || scoreForm.scores[cat.key] < 1) {
        toast.error(`Please rate ${cat.label}`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/performance/scores`, scoreForm, { headers });
      toast.success(`Score submitted! ${res.data.current_tier} tier (${res.data.current_pct}%)`);
      if (res.data.alert_triggered) {
        toast.warning('Performance alert triggered for this cleaner');
      }
      setScoreOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit score');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      await axios.put(`${API_URL}/api/performance/alerts/${alertId}/acknowledge`, {}, { headers });
      setAlerts(a => a.filter(x => x.id !== alertId));
      toast.success('Alert acknowledged');
    } catch { toast.error('Failed to acknowledge'); }
  };

  const scoreTotal = useMemo(
    () => Object.values(scoreForm.scores).reduce((a, b) => a + b, 0),
    [scoreForm.scores]
  );
  const filtered = cleaners.filter(c => {
    if (tierFilter !== 'all' && c.tier.toLowerCase() !== tierFilter) return false;
    if (search && !c.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const tierCounts = { Elite: 0, Strong: 0, Watch: 0, Risk: 0, Unrated: 0 };
  cleaners.forEach(c => { tierCounts[c.tier] = (tierCounts[c.tier] || 0) + 1; });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950">
        <Loader2 className="h-8 w-8 animate-spin text-[#F56F05]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950" data-testid="admin-performance-page">
      <header className="sticky top-0 z-40 bg-zinc-950/95 backdrop-blur-md border-b border-zinc-800/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white hover:bg-zinc-800 -ml-2"
              onClick={back} data-testid="back-btn">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <Separator orientation="vertical" className="h-5 bg-zinc-800" />
            <h1 className="font-bold text-white text-sm sm:text-base flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-[#F56F05]" /> Performance Tracker
            </h1>
          </div>
          <Button onClick={openScoreDialog} className="bg-[#F56F05] hover:bg-[#C2410C] text-white text-xs h-9"
            data-testid="score-job-btn">
            <Star className="h-3.5 w-3.5 mr-1" /> Score Job
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Alerts Banner */}
        <AnimatePresence>
          {alerts.length > 0 && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              <Card className="border-0 bg-red-500/10 border border-red-500/20" data-testid="alerts-banner">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Bell className="h-4 w-4 text-red-400" />
                    <span className="text-sm font-bold text-red-300">{alerts.length} Active Alert{alerts.length > 1 ? 's' : ''}</span>
                  </div>
                  <div className="space-y-2">
                    {alerts.map(a => (
                      <div key={a.id} className="flex items-center justify-between bg-red-500/10 rounded-lg px-3 py-2" data-testid={`alert-${a.id}`}>
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="h-3.5 w-3.5 text-red-400" />
                          <span className="text-sm text-red-200">{a.cleaner_name}: {a.message}</span>
                        </div>
                        <Button variant="ghost" size="sm" className="text-red-400 hover:text-white hover:bg-red-500/20 h-7 text-xs"
                          onClick={() => handleAcknowledgeAlert(a.id)} data-testid={`ack-alert-${a.id}`}>
                          <BellOff className="h-3 w-3 mr-1" /> Dismiss
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tier Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="tier-summary">
          {['Elite', 'Strong', 'Watch', 'Risk', 'Unrated'].map(t => (
            <Card key={t} className={`border-0 cursor-pointer transition-all hover:scale-[1.02] ${tierFilter === t.toLowerCase() ? 'ring-2 ring-[#F56F05]' : ''} bg-zinc-900`}
              onClick={() => setTierFilter(tierFilter === t.toLowerCase() ? 'all' : t.toLowerCase())}
              data-testid={`tier-card-${t.toLowerCase()}`}>
              <CardContent className="p-3 text-center">
                <Badge className={`${TIER_STYLES[t]} text-[10px] mb-1.5`}>{t}</Badge>
                <p className="text-2xl font-bold text-white">{tierCounts[t]}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input placeholder="Search cleaners..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-10 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600" data-testid="search-input" />
        </div>

        {/* Cleaner List */}
        <div className="space-y-2" data-testid="cleaner-list">
          {filtered.map((c, i) => (
            <motion.div key={c.cleaner_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}>
              <Card className="border-0 bg-zinc-900 hover:bg-zinc-800/80 transition-all cursor-pointer active:scale-[0.99]"
                onClick={() => openCleanerDetail(c)} data-testid={`cleaner-row-${c.cleaner_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    {/* Score Ring */}
                    <div className="relative flex-shrink-0">
                      <ScoreRing pct={c.average_pct} size={56} tier={c.tier} />
                      <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-white">
                        {c.average_pct > 0 ? `${Math.round(c.average_pct)}%` : '—'}
                      </span>
                    </div>
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-white text-sm truncate">{c.name}</p>
                        <Badge className={`${TIER_STYLES[c.tier]} text-[10px]`}>{c.tier}</Badge>
                        {c.trend === 'improving' && <TrendingUp className="h-3.5 w-3.5 text-emerald-400" />}
                        {c.trend === 'declining' && <TrendingDown className="h-3.5 w-3.5 text-red-400" />}
                        {c.trend === 'stable' && c.total_scored_jobs > 0 && <Minus className="h-3.5 w-3.5 text-zinc-500" />}
                      </div>
                      <p className="text-xs text-zinc-500 mt-0.5">
                        {c.total_scored_jobs} job{c.total_scored_jobs !== 1 ? 's' : ''} scored
                        {c.average_score > 0 && ` | Avg ${c.average_score}/${25}`}
                      </p>
                    </div>
                    {/* Sparkline */}
                    <div className="hidden sm:flex items-end gap-0.5 h-8">
                      {c.last_10_scores.slice().reverse().map((s, j) => (
                        <div key={s.id || `bar-${j}`} className={`w-1.5 rounded-t transition-all ${
                          s.pct >= 90 ? 'bg-emerald-500' : s.pct >= 80 ? 'bg-blue-500' : s.pct >= 70 ? 'bg-amber-500' : 'bg-red-500'
                        }`} style={{ height: `${Math.max(s.pct * 0.32, 4)}px` }} />
                      ))}
                    </div>
                    <ChevronRight className="h-4 w-4 text-zinc-600 flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
          {filtered.length === 0 && (
            <div className="text-center py-12">
              <Trophy className="h-10 w-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500">No cleaners match your filters</p>
            </div>
          )}
        </div>
      </main>

      {/* Score Job Dialog */}
      <Dialog open={scoreOpen} onOpenChange={setScoreOpen}>
        <DialogContent className="max-w-md bg-zinc-900 border-zinc-800 text-white max-h-[90vh] overflow-y-auto" data-testid="score-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Star className="h-5 w-5 text-[#F56F05]" /> Score Completed Job
            </DialogTitle>
            <DialogDescription className="text-zinc-400">Rate cleaner performance across 5 categories</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 pt-2">
            {/* Appointment Selection */}
            <div>
              <Label className="text-zinc-300 text-xs">Completed Appointment</Label>
              {apptLoading ? (
                <div className="flex items-center gap-2 py-3"><Loader2 className="h-4 w-4 animate-spin text-[#F56F05]" /><span className="text-sm text-zinc-500">Loading...</span></div>
              ) : (
                <Select onValueChange={handleApptSelect} value={scoreForm.appointment_id}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1" data-testid="appt-select">
                    <SelectValue placeholder="Select a completed job..." />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {completedAppts.map(a => (
                      <SelectItem key={a.id} value={a.id} className="text-white">
                        {a.service?.name || 'Job'} — {new Date(a.start_time).toLocaleDateString()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {scoreForm.appointment_id && (
              <>
                {/* Category Ratings */}
                {CATEGORIES.map(cat => (
                  <div key={cat.key} className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <cat.icon className="h-4 w-4 text-[#F56F05]" />
                      <Label className="text-zinc-200 text-sm font-semibold">{cat.label}</Label>
                    </div>
                    <p className="text-[11px] text-zinc-500 mb-1">{cat.desc}</p>
                    <StarRating value={scoreForm.scores[cat.key] || 0}
                      onChange={v => setScoreForm(f => ({ ...f, scores: { ...f.scores, [cat.key]: v } }))}
                      disabled={submitting} />
                  </div>
                ))}

                {/* Total Preview */}
                {Object.keys(scoreForm.scores).length === 5 && (
                  <div className="bg-zinc-800 rounded-xl p-3 text-center">
                    <p className="text-xs text-zinc-500 mb-0.5">Total Score</p>
                    <p className="text-2xl font-bold text-white" data-testid="score-total">
                      {scoreTotal}/25
                    </p>
                    <p className="text-xs text-zinc-500">
                      ({Math.round((scoreTotal / 25) * 100)}%)
                    </p>
                  </div>
                )}

                {/* Notes */}
                <div>
                  <Label className="text-zinc-300 text-xs">Notes (optional)</Label>
                  <Textarea value={scoreForm.notes} onChange={e => setScoreForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="Performance notes..." rows={2}
                    className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-600 mt-1" data-testid="score-notes" />
                </div>

                <Button onClick={handleSubmitScore} disabled={submitting}
                  className="w-full bg-[#F56F05] hover:bg-[#C2410C] text-white h-11 font-bold" data-testid="submit-score-btn">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                  Submit Score
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Cleaner Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg bg-zinc-900 border-zinc-800 text-white max-h-[90vh] overflow-y-auto" data-testid="cleaner-detail-dialog">
          {detailLoading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-[#F56F05]" /></div>
          ) : detailData ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-white">{detailData.name}</DialogTitle>
                <DialogDescription className="text-zinc-400">{detailData.email}</DialogDescription>
              </DialogHeader>
              <div className="space-y-5 pt-2">
                {/* Score + Tier Header */}
                <div className={`bg-gradient-to-br ${TIER_BG[detailData.tier] || TIER_BG.Unrated} rounded-xl p-5 text-center`} data-testid="detail-tier-card">
                  <Badge className="bg-white/20 text-white border-white/30 text-xs mb-2">{detailData.tier} Tier</Badge>
                  <p className="text-4xl font-black text-white">{detailData.average_pct}%</p>
                  <p className="text-sm text-white/60">{detailData.average_score}/25 avg from last {detailData.last_10_scores.length} jobs</p>
                  <div className="flex items-center justify-center gap-1 mt-1 text-white/70 text-xs">
                    {detailData.trend === 'improving' && <><TrendingUp className="h-3.5 w-3.5" /> Improving</>}
                    {detailData.trend === 'declining' && <><TrendingDown className="h-3.5 w-3.5" /> Declining</>}
                    {detailData.trend === 'stable' && <><Minus className="h-3.5 w-3.5" /> Stable</>}
                  </div>
                </div>

                {/* Category Breakdown */}
                <div data-testid="category-breakdown">
                  <h3 className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-3">Category Averages</h3>
                  <div className="space-y-2">
                    {CATEGORIES.map(cat => {
                      const val = detailData.category_averages[cat.key] || 0;
                      const pct = (val / 5) * 100;
                      return (
                        <div key={cat.key} className="flex items-center gap-3">
                          <cat.icon className="h-4 w-4 text-zinc-500 flex-shrink-0" />
                          <span className="text-xs text-zinc-400 w-28 flex-shrink-0">{cat.label}</span>
                          <div className="flex-1 h-2 bg-zinc-800 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full transition-all duration-700 ${
                              pct >= 80 ? 'bg-emerald-500' : pct >= 60 ? 'bg-amber-500' : 'bg-red-500'
                            }`} style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-xs font-bold text-zinc-300 w-8 text-right">{val}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Last 10 Scores */}
                {detailData.last_10_scores.length > 0 && (
                  <div data-testid="score-history">
                    <h3 className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-3">Recent Scores</h3>
                    <div className="space-y-1.5">
                      {detailData.last_10_scores.map(s => (
                        <div key={s.id} className="flex items-center justify-between bg-zinc-800/60 rounded-lg px-3 py-2">
                          <div>
                            <p className="text-xs text-zinc-300 font-medium">{s.service_name || 'Job'}</p>
                            <p className="text-[10px] text-zinc-600">{new Date(s.created_at).toLocaleDateString()}{s.client_name && ` | ${s.client_name}`}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-bold ${s.pct >= 90 ? 'text-emerald-400' : s.pct >= 80 ? 'text-blue-400' : s.pct >= 70 ? 'text-amber-400' : 'text-red-400'}`}>
                              {s.total_score}/25
                            </span>
                            <Badge className={`text-[10px] ${TIER_STYLES[s.pct >= 90 ? 'Elite' : s.pct >= 80 ? 'Strong' : s.pct >= 70 ? 'Watch' : 'Risk']}`}>
                              {s.pct}%
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Client Reviews */}
                {detailData.reviews?.length > 0 && (
                  <div data-testid="client-reviews">
                    <h3 className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-3">Client Reviews</h3>
                    <div className="space-y-2">
                      {detailData.reviews.map(r => (
                        <div key={r.id || r.created_at} className="bg-zinc-800/40 rounded-lg p-3">
                          <div className="flex items-center gap-1 mb-1">
                            {[...Array(5)].map((_, i) => (
                              // eslint-disable-next-line react/no-array-index-key
                              <Star key={i} className={`h-3 w-3 ${i < (r.rating || 0) ? 'text-amber-400 fill-amber-400' : 'text-zinc-700'}`} />
                            ))}
                          </div>
                          {r.comment && <p className="text-xs text-zinc-300">{r.comment}</p>}
                          <p className="text-[10px] text-zinc-600 mt-1">{new Date(r.created_at).toLocaleDateString()}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
